/**
 * React Query + SSE для серверных уведомлений.
 */

import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getToken } from "@/lib/api-client-v2";
import {
  notificationsApi,
  type PreferenceItem,
  type UserNotificationListItem,
} from "@/services/notifications-api.service";

export const notifyKeys = {
  unread: (token: string | null) => ["notifications-unread", token] as const,
  list: (token: string | null, unreadOnly: boolean, page: number) =>
    ["notifications-list", token, unreadOnly, page] as const,
  preferences: (token: string | null) => ["notifications-preferences", token] as const,
  devices: (token: string | null) => ["notifications-devices", token] as const,
};

export function useUnreadNotifications() {
  const token = getToken();
  return useQuery({
    queryKey: notifyKeys.unread(token),
    queryFn: async () => (await notificationsApi.unreadCount()).count,
    enabled: !!token,
    refetchInterval: 60_000,
  });
}

export function useNotificationsList(opts?: {
  unreadOnly?: boolean;
  limit?: number;
  offset?: number;
}) {
  const token = getToken();
  const unreadOnly = opts?.unreadOnly ?? false;
  const limit = opts?.limit ?? 20;
  const offset = opts?.offset ?? 0;
  return useQuery({
    queryKey: notifyKeys.list(token, unreadOnly, Math.floor(offset / limit)),
    queryFn: () =>
      notificationsApi.list({
        unread_only: unreadOnly,
        limit,
        offset,
      }),
    enabled: !!token,
  });
}

export function useNotificationMutations() {
  const qc = useQueryClient();
  const token = getToken();

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["notifications-unread"] });
    void qc.invalidateQueries({ queryKey: ["notifications-list"] });
  }, [qc]);

  const markRead = useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess: invalidate,
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: invalidate,
  });

  return { markRead, markAllRead, invalidate, token };
}

export function useNotificationPreferences() {
  const token = getToken();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: notifyKeys.preferences(token),
    queryFn: async () => (await notificationsApi.getPreferences()).items,
    enabled: !!token,
  });
  const mutation = useMutation({
    mutationFn: (items: PreferenceItem[]) => notificationsApi.putPreferences(items),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notifyKeys.preferences(token) });
    },
  });
  return { ...query, save: mutation };
}

export function useNotificationDevices() {
  const token = getToken();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: notifyKeys.devices(token),
    queryFn: () => notificationsApi.listDevices(),
    enabled: !!token,
  });
  const remove = useMutation({
    mutationFn: (id: number) => notificationsApi.deleteDevice(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notifyKeys.devices(token) });
    },
  });
  return { ...query, remove };
}

/**
 * SSE: короткий токен, ротация ~40с, на refresh — invalidate unread/list.
 */
export function useNotificationsStream(enabled = true) {
  const qc = useQueryClient();
  const esRef = useRef<EventSource | null>(null);
  const timerRef = useRef<number | null>(null);
  const rotateRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !getToken()) return;

    let cancelled = false;

    const connect = async () => {
      try {
        const { token } = await notificationsApi.sseToken();
        if (cancelled) return;
        if (esRef.current) {
          esRef.current.close();
          esRef.current = null;
        }
        const url = `/api/v1/notifications/stream?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        esRef.current = es;
        es.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data) as { type?: string };
            if (data.type === "init" || data.type === "refresh") {
              void qc.invalidateQueries({ queryKey: ["notifications-unread"] });
              void qc.invalidateQueries({ queryKey: ["notifications-list"] });
            }
          } catch {
            /* ignore */
          }
        };
        es.onerror = () => {
          es.close();
          esRef.current = null;
        };
      } catch {
        /* fail-soft */
      }
    };

    void connect();
    rotateRef.current = window.setInterval(() => {
      void connect();
    }, 40_000);

    return () => {
      cancelled = true;
      if (rotateRef.current) window.clearInterval(rotateRef.current);
      if (timerRef.current) window.clearInterval(timerRef.current);
      esRef.current?.close();
      esRef.current = null;
    };
  }, [enabled, qc]);
}

export type { UserNotificationListItem };
